import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// Import Types
import type {
    AddNewTemplateListPayload,
    ChangeTemplateValueForAllLanguagesPayload,
    ChangeTemplateValuePayload,
    ClearTemplateListPayload,
    SetTemplateAsDefaultPayload,
    SetUIConfigPayload,
    TemplateInitialData,
} from 'types';
// Import Services
import { createTemplateInitialData } from 'services/template';
// Import Constants
import { TemplateInputTypes } from 'constants/common';

export interface TemplateSliceState {
    data: Record<string, TemplateInitialData>;
}

export const initialState: TemplateSliceState = {
    data: {},
};

export const templateSlice = createSlice({
    name: 'template',
    initialState,
    reducers: (create) => ({
        addNewTemplateList: create.reducer((state, { payload }: PayloadAction<AddNewTemplateListPayload>) => {
            const { type, data: dataArray, template, isCascade } = payload;
            dataArray.forEach(({ data }) => {
                state.data = {
                    ...state.data,
                    [data.Id]: createTemplateInitialData[type](data.Id, template, { isCascade }),
                };
            });
        }),
        setTemplateAsDefault: create.reducer((state, { payload }: PayloadAction<SetTemplateAsDefaultPayload>) => {
            const { targetId, type, template } = payload;
            state.data = {
                ...state.data,
                [targetId]: {
                    ...state.data[targetId],
                    ...createTemplateInitialData[type](null, template),
                    [TemplateInputTypes.Category]: state.data[targetId][TemplateInputTypes.Category] || [],
                },
            };
        }),
        changeTemplateValue: create.reducer((state, { payload }: PayloadAction<ChangeTemplateValuePayload>) => {
            const { templateLangKey, propertyName, value } = payload;
            if (state.data[templateLangKey]) {
                state.data = {
                    ...state.data,
                    [templateLangKey]: {
                        ...state.data[templateLangKey],
                        [propertyName]: value,
                    },
                };
            }
        }),
        changeTemplateValueForAllLanguages: create.reducer(
            (state, { payload }: PayloadAction<ChangeTemplateValueForAllLanguagesPayload>) => {
                const { propertyName, value } = payload;
                Object.keys(state.data).forEach((templateLangKey) => {
                    if (state.data[templateLangKey]) {
                        state.data = {
                            ...state.data,
                            [templateLangKey]: {
                                ...state.data[templateLangKey],
                                [propertyName]: value,
                            },
                        };
                    }
                });
            },
        ),
        setTemplateValue: create.reducer((state, { payload }: PayloadAction<Record<string, TemplateInitialData>>) => {
            state.data = { ...payload };
        }),
        setTemplateInitialData: create.reducer((state, { payload }: PayloadAction<TemplateInitialData>) => {
            state.data = { defaultLang: payload };
        }),
        batchChangeTemplateValueForAllLanguages: create.reducer(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (state, { payload }: PayloadAction<{ propertyName: string; value: any }[]>) => {
                payload.forEach(({ propertyName, value }) => {
                    Object.keys(state.data).forEach((templateLangKey) => {
                        if (state.data[templateLangKey]) {
                            state.data[templateLangKey][propertyName] = value;
                        }
                    });
                });
            },
        ),
        clearTemplateList: create.reducer((state, { payload }: PayloadAction<ClearTemplateListPayload>) => {
            const { templateType, isCascade } = payload;
            state.data = {
                defaultLang: {
                    ...state.data.defaultLang,
                    ...createTemplateInitialData[templateType](null, null, { isCascade }),
                },
            };
        }),
        deleteTemplateLanguage: create.reducer((state, { payload }: PayloadAction<string>) => {
            delete state.data[payload];
        }),
        setUIConfig: create.reducer((state, { payload }: PayloadAction<SetUIConfigPayload>) => {
            const { templateLangKey, editorKey, editorConfig } = payload;
            if (state.data[templateLangKey]) {
                state.data = {
                    ...state.data,
                    [templateLangKey]: {
                        ...state.data[templateLangKey],
                        UIConfig: {
                            ...state.data[templateLangKey].UIConfig,
                            [editorKey]: editorConfig,
                        },
                    },
                };
            }
        }),
    }),
    selectors: {
        selectTemplateState: (template) => template.data,
    },
});

export const {
    addNewTemplateList,
    setTemplateAsDefault,
    changeTemplateValue,
    changeTemplateValueForAllLanguages,
    setTemplateValue,
    setTemplateInitialData,
    batchChangeTemplateValueForAllLanguages,
    clearTemplateList,
    deleteTemplateLanguage,
    setUIConfig,
} = templateSlice.actions;
export const { selectTemplateState } = templateSlice.selectors;
